import { getSmoothStepPath, Position } from '@xyflow/react';
import { estimateTableHeight } from '@hyphen-db-studio/diagram-engine';
import type { RefType, Schema } from '@hyphen-db-studio/schema-core';
import { downloadTextFile } from './export';
import { tintedTableColors } from './tableStyle';
import { useDiagramViewStore } from '../store/useDiagramViewStore';

/**
 * ER diagrams are exported by drawing the SVG straight from the schema, not by screenshotting the canvas.
 *
 * The canvas-screenshot route (html-to-image) clones every DOM node of the diagram, copies the computed style of
 * each one, and rasterises the lot on the main thread. With hundreds of columns that freezes the tab, and the
 * resulting bitmap can exceed the browser's canvas limits and silently come out blank. Building the SVG from the
 * model is a few milliseconds of string work, stays vector, and the PNG is a single native rasterisation of it.
 */

const NODE_WIDTH = 240;
const HEADER_HEIGHT = 40;
const ROW_HEIGHT = 28;
const GROUP_PADDING = 28;
const GROUP_LABEL_HEIGHT = 24;
const EXPORT_PADDING = 48;
const FONT = "Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

/** Browsers refuse canvases past roughly these sizes (and some come out blank), so PNGs are scaled to fit. */
const MAX_PNG_SIDE = 12000;
const MAX_PNG_AREA = 50_000_000;

const CARDINALITY: Record<RefType, [string, string]> = {
  '>': ['*', '1'],
  '<': ['1', '*'],
  '-': ['1', '1'],
  '<>': ['*', '*'],
};

type Theme = 'light' | 'dark';

const PALETTE = {
  light: {
    background: '#f5f7fb', card: '#ffffff', cardBorder: '#cbd5e1', header: '#f1f5f9', headerText: '#1e293b',
    rowText: '#1e293b', typeText: '#94a3b8', divider: '#f1f5f9', edge: '#94a3b8', label: '#64748b', halo: '#ffffff',
  },
  dark: {
    background: '#070b14', card: '#1e293b', cardBorder: '#475569', header: '#0f172a', headerText: '#f1f5f9',
    rowText: '#f1f5f9', typeText: '#64748b', divider: '#334155', edge: '#6b7280', label: '#cbd5e1', halo: '#0f172a',
  },
} as const;

const esc = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Cuts text to roughly `maxWidth` pixels (SVG has no automatic ellipsis). */
function fit(text: string, maxWidth: number, charWidth: number): string {
  const maxChars = Math.max(Math.floor(maxWidth / charWidth), 1);
  return text.length <= maxChars ? text : `${text.slice(0, Math.max(maxChars - 1, 1))}…`;
}

// lucide "key" and "link-2" outlines, drawn at 12px
const KEY_ICON =
  '<g transform="scale(0.5)" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="7.5" cy="15.5" r="5.5"/><path d="m21 2-9.6 9.6"/><path d="m15.5 7.5 3 3L22 7l-3-3"/></g>';
const LINK_ICON =
  '<g transform="scale(0.5)" fill="none" stroke="#0ea5e9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 17H7A5 5 0 0 1 7 7h2"/><path d="M15 7h2a5 5 0 1 1 0 10h-2"/><line x1="8" x2="16" y1="12" y2="12"/></g>';

export function buildErSvg(schema: Schema, theme: Theme): { svg: string; width: number; height: number } {
  const p = PALETTE[theme];
  const isDark = theme === 'dark';
  const tables = schema.tables.map((t) => ({ table: t, x: t.position?.x ?? 0, y: t.position?.y ?? 0, h: estimateTableHeight(t.columns.length) }));
  const byId = new Map(tables.map((t) => [t.table.id, t]));
  const groupColor = new Map(schema.groups.map((g) => [g.id, g.color]));

  const fkColumns = new Set<string>();
  for (const rel of schema.relationships) fkColumns.add(rel.type === '<' ? rel.toColumnId : rel.fromColumnId);

  // ---- group boxes (same geometry as the canvas)
  const groupBoxes = schema.groups.flatMap((g) => {
    const members = tables.filter((t) => t.table.groupId === g.id);
    if (!members.length) return [];
    const minX = Math.min(...members.map((m) => m.x));
    const minY = Math.min(...members.map((m) => m.y));
    const maxX = Math.max(...members.map((m) => m.x + NODE_WIDTH));
    const maxY = Math.max(...members.map((m) => m.y + m.h));
    return [{ g, x: minX - GROUP_PADDING, y: minY - GROUP_PADDING - GROUP_LABEL_HEIGHT, w: maxX - minX + GROUP_PADDING * 2, h: maxY - minY + GROUP_PADDING * 2 + GROUP_LABEL_HEIGHT }];
  });

  // ---- bounds
  const xs = [...tables.map((t) => t.x), ...groupBoxes.map((b) => b.x)];
  const ys = [...tables.map((t) => t.y), ...groupBoxes.map((b) => b.y)];
  const x2 = [...tables.map((t) => t.x + NODE_WIDTH), ...groupBoxes.map((b) => b.x + b.w)];
  const y2 = [...tables.map((t) => t.y + t.h), ...groupBoxes.map((b) => b.y + b.h)];
  const minX = xs.length ? Math.min(...xs) : 0;
  const minY = ys.length ? Math.min(...ys) : 0;
  const width = Math.max(Math.ceil((x2.length ? Math.max(...x2) : 0) - minX + EXPORT_PADDING * 2), 1);
  const height = Math.max(Math.ceil((y2.length ? Math.max(...y2) : 0) - minY + EXPORT_PADDING * 2), 1);

  const parts: string[] = [];

  for (const { g, x, y, w, h } of groupBoxes) {
    const color = g.color ?? '#6366f1';
    parts.push(
      `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="12" fill="${color}" fill-opacity="${isDark ? 0.24 : 0.15}" stroke="${color}" stroke-width="2" stroke-dasharray="6 4"/>`,
      `<text x="${x + 12}" y="${y + 17}" font-size="12" font-weight="600" letter-spacing="0.6" fill="${color}">${esc(g.name.toUpperCase())}</text>`,
    );
  }

  // ---- relationships (behind the tables, like the canvas)
  const rowCenter = (entry: (typeof tables)[number], columnId: string) => {
    const index = Math.max(entry.table.columns.findIndex((c) => c.id === columnId), 0);
    return entry.y + HEADER_HEIGHT + ROW_HEIGHT * index + ROW_HEIGHT / 2;
  };
  const labelStyle = `font-size="10" font-weight="600" text-anchor="middle" fill="${p.label}" stroke="${p.halo}" stroke-width="3" paint-order="stroke fill"`;
  for (const rel of schema.relationships) {
    const from = byId.get(rel.fromTableId);
    const to = byId.get(rel.toTableId);
    if (!from || !to) continue;
    const sourceX = from.x + NODE_WIDTH;
    const sourceY = rowCenter(from, rel.fromColumnId);
    const targetX = to.x;
    const targetY = rowCenter(to, rel.toColumnId);
    const [path] = getSmoothStepPath({
      sourceX, sourceY, sourcePosition: Position.Right, targetX, targetY, targetPosition: Position.Left, borderRadius: 12,
    });
    const [fromLabel, toLabel] = CARDINALITY[rel.type];
    parts.push(
      `<path d="${path}" fill="none" stroke="${p.edge}" stroke-width="1.5"/>`,
      `<text x="${sourceX}" y="${sourceY - 8}" ${labelStyle}>${fromLabel}</text>`,
      `<text x="${targetX}" y="${targetY - 8}" ${labelStyle}>${toLabel}</text>`,
    );
  }

  // ---- tables
  for (const { table, x, y, h } of tables) {
    const tint = table.color ?? (table.groupId ? groupColor.get(table.groupId) : undefined);
    const colors = tint ? tintedTableColors(tint, isDark) : null;
    const cardFill = colors?.body ?? p.card;
    const border = colors?.border ?? p.cardBorder;
    const headerFill = colors?.header ?? p.header;
    const headerText = colors?.headerText ?? p.headerText;
    const clip = `clip-${esc(table.id)}`;

    parts.push(
      `<g transform="translate(${x} ${y})">`,
      `<clipPath id="${clip}"><rect width="${NODE_WIDTH}" height="${h}" rx="12"/></clipPath>`,
      `<rect width="${NODE_WIDTH}" height="${h}" rx="12" fill="${cardFill}"/>`,
      `<g clip-path="url(#${clip})">`,
      `<rect width="${NODE_WIDTH}" height="${HEADER_HEIGHT}" fill="${headerFill}"/>`,
      `</g>`,
      `<text x="14" y="25" font-size="14" font-weight="600" fill="${headerText}">${esc(fit(table.name, NODE_WIDTH - 28, 7.6))}</text>`,
    );
    table.columns.forEach((col, i) => {
      const top = HEADER_HEIGHT + i * ROW_HEIGHT;
      if (i > 0) parts.push(`<line x1="0" x2="${NODE_WIDTH}" y1="${top}" y2="${top}" stroke="${p.divider}"/>`);
      if (col.settings.pk) parts.push(`<g transform="translate(12 ${top + 8})">${KEY_ICON}</g>`);
      else if (fkColumns.has(col.id)) parts.push(`<g transform="translate(12 ${top + 8})">${LINK_ICON}</g>`);
      const nullable = !col.settings.pk && !col.settings.notNull;
      const typeText = fit(col.type, 90, 6.2) + (nullable ? '?' : '');
      const typeWidth = typeText.length * 6.2;
      parts.push(
        `<text x="30" y="${top + 18}" font-size="12" font-weight="500" fill="${p.rowText}">${esc(fit(col.name, NODE_WIDTH - 30 - 14 - typeWidth - 8, 6.6))}</text>`,
        `<text x="${NODE_WIDTH - 12}" y="${top + 18}" font-size="12" text-anchor="end" fill="${p.typeText}">${esc(typeText)}</text>`,
      );
    });
    parts.push(
      `<line x1="0" x2="${NODE_WIDTH}" y1="${HEADER_HEIGHT}" y2="${HEADER_HEIGHT}" stroke="${border}"/>`,
      `<rect width="${NODE_WIDTH}" height="${h}" rx="12" fill="none" stroke="${border}"/>`,
      `</g>`,
    );
  }

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" font-family="${FONT}">` +
    `<rect width="${width}" height="${height}" fill="${p.background}"/>` +
    `<g transform="translate(${EXPORT_PADDING - minX} ${EXPORT_PADDING - minY})">${parts.join('')}</g></svg>`;
  return { svg, width, height };
}

/** Lets a toast / spinner paint before the (brief) synchronous work starts. */
const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export async function exportErAsSvg(schema: Schema, filename: string, theme: Theme) {
  useDiagramViewStore.getState().showToast('Preparing SVG…');
  await nextFrame();
  const { svg } = buildErSvg(schema, theme);
  downloadTextFile(filename, svg, 'image/svg+xml');
}

export async function exportErAsPng(schema: Schema, filename: string, theme: Theme) {
  const { showToast } = useDiagramViewStore.getState();
  showToast('Preparing PNG…');
  await nextFrame();
  const { svg, width, height } = buildErSvg(schema, theme);

  // Crisp (2x) when it fits, otherwise as large as the browser's canvas limits allow.
  const scale = Math.min(2, MAX_PNG_SIDE / Math.max(width, height), Math.sqrt(MAX_PNG_AREA / (width * height)));

  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    await image.decode();

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(Math.round(width * scale), 1);
    canvas.height = Math.max(Math.round(height * scale), 1);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not create a canvas for the export.');
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const png = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('The browser could not encode the PNG.'))), 'image/png'),
    );
    downloadBlob(png, filename);
    if (scale < 1) showToast('Large diagram: PNG exported at reduced size. Use SVG for full quality.');
  } finally {
    URL.revokeObjectURL(url);
  }
}
